<template>
    <!-- Confirm Password -->
    <div v-show="!showVerificationModal" class="row">
        <div class="col-sm-12 mb-3" v-if="header">
            <div class="d-flex flex-column gap-3 text-center align-items-center">
	            <i class="bi w-auto h-auto fs-large bi-envelope-at"></i>
                <h3 class="fw-semi-bold m-0">Change Email Address</h3>
                <p class="text-secondary m-0">Please enter a new email address in the text field.</p>
            </div>
            <div v-if="formErrors.email" class="validation-error mt-0 text-center" v-html="formErrors.email"></div>
        </div>

        <div ref="changeEmailForm" class="col-12">
            <div class="d-flex align-items-center justify-content-between rounded-pill border py-2 px-3 input-container border-active-darken mb-3" :class="formErrors.email ? 'border-danger' : 'border-secondary'">
	            <label for="email">
		            <i class="me-3 dd-icon bi bi-envelope-at"></i>
	            </label>
	            <input v-model="email" autofocus type="email" ref="emailChange" name="email" class="form-control m-0 border-0 fs-5 p-0 lh-fixed" placeholder="Enter New Email Address" @input="removeFormErrorKey('email')" @keyup.enter="verifyNewEmail">
            </div>

            <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center ">
                <button class="btn btn-outline-secondary rounded-pill py-2 px-3" @click="close">Cancel</button>
                <button v-if="!autoSubmit" class="btn btn-dark rounded-pill py-2 px-3" @click="verifyNewEmail" :disabled="hasFormErrors">Save Changes</button>
            </div>
        </div>
    </div>
	<Verification v-if="showVerificationModal" type="email" :mfa_email="email" :timer="expirySeconds" @confirmation-status="changeEmail"></Verification>

</template>

<script setup>
import { computed, watch, ref, onMounted, nextTick } from "vue";
import apiClient from "@/utils/axios.js";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import {useHttpErrorManager} from "@/composables/useHttpErrorManager.js";
import Verification from "@/components/GenericModalAndViews/Verification.vue";
import { useAuthStore } from "@/stores/auth-store.js";
import { useToast } from "@/composables/useToast.js";

const props = defineProps({
	header: { type: Boolean, required: false, default: true },
	autoSubmit: { type: Boolean, required: false, default: false }
});

const emits = defineEmits(['closeChangeEmail']);

const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const changeEmailForm = ref(null);

const user = ref(auth.user);
watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

const email = ref('');
const formErrors = ref({});

const showVerificationModal = ref(false);
const expirySeconds = ref(300);

const emailChange = ref(null);
onMounted(() => {
	nextTick(() => {
		emailChange.value.focus();
	});
});

const removeFormErrorKey = (key) => {
    delete formErrors.value[key];
};

const hasFormErrors = computed(() => {
    return Object.keys(formErrors.value).length > 0;
});

const emailValidated = ref(false);
const verifyNewEmail = async () => {
	try{
		helper.loaderOn(changeEmailForm.value);
		await validateEmail();
		if(!emailValidated.value) {
			helper.loaderOff(changeEmailForm.value);
			return;
		}


		const response = await apiClient.post('auth/resendVerificationCodeEmail', {
			email: email.value,
			user_id: user.value.id,
			purpose: 'email_verification',
			from: 'change_email',
		});
		if (response.status >= 200 && response.status < 300) {
			showVerificationModal.value = true;
			expirySeconds.value = response.data.expire_seconds;
		}
	}
	catch (error) {
		showVerificationModal.value = false;
		helper.loaderOff(changeEmailForm.value);
	}

}

const validateEmail = async () => {
	emailValidated.value = false;
	if (!email.value || (email.value && email.value.trim() === '')) {
		formErrors.value.email = 'Email Address is required.';
		return;
	}
	try{
		const response = await apiClient.post('auth/user/checkEmail', {
			email: email.value
		});
		if (response.status >= 200 && response.status < 300) {
			emailValidated.value = true;
		}
	}
	catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error);
		if (error.response.status === 422) {
			formErrors.value = errorObj;
			removeFormErrorKey('full_message');
			removeFormErrorKey('alt_message');
			removeFormErrorKey('err_message');
		}
		emailValidated.value = false;
	}

}

const changeEmail = async (confirmation) => {
	showVerificationModal.value = false;
	if(!confirmation[0]) {
		helper.loaderOff(changeEmailForm.value);
		return;
	}
    helper.loaderOn(changeEmailForm.value);

    try {
        const response = await apiClient.post('auth/user/changeEmail', {
            email: email.value
        });

        if (response.status >= 200 && response.status < 300) {
			displayToast('Email Address Changed Successfully!', 'outline-success', 8000);
			emits('closeChangeEmail');
        }
	    helper.loaderOff(changeEmailForm.value);
    }
    catch (error) {
        helper.loaderOff(changeEmailForm.value);
        let errorObj = await useHttpErrorManager().handleError(error);
        if (error.response.status === 422) {
            formErrors.value = errorObj;
	        removeFormErrorKey('full_message');
            removeFormErrorKey('alt_message');
            removeFormErrorKey('err_message');
        }
    }
}

const close = async () => {
	emits('closeChangeEmail');
}

defineExpose({
    close
});
</script>
